/**
 * @file MilkPowderCostHistoryModal.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-04
 * @modified 2026-07-21
 * @description Read-only weekly cost-price history for a milk powder product,
 *              newest week first. Table sits above a rainbow line chart of the
 *              cost (solid between consecutive records, dotted across gaps) with a
 *              faded rainbow fill and a dot on every recorded week. A 12-month
 *              window pages back/forward with chevrons; the current window also
 *              carries a dotted forward projection (recent trend blended with the
 *              prior-years' seasonal pattern) drawn only past today. Hover shows a
 *              crosshair + price pill.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useThemeColors from '../hooks/useThemeColors';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import ChartWindowNav from './ChartWindowNav';
import useTimeWindow from '../hooks/useTimeWindow';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatters';
import './TableStyles.css';

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const DAY = 86400000;
const WEEK = 7 * DAY;

// Parse a 'YYYY-MM-DD' (or ISO) week_commencing into a local Date at midnight.
function parseDay(s) {
  return new Date(String(s).slice(0, 10) + 'T00:00:00');
}

function shortDate(d) {
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

// Project the next 3 weeks. Trend/seasonality are derived from the recorded
// points, but the projection is anchored at `fromDate`/`fromCost` (the end of
// the carried-forward series) so the dotted line extends 3 weeks beyond it
// rather than overlapping weeks already plotted. Heuristic — drawn dotted.
function buildForecast(points, fromDate, fromCost) {
  if (points.length < 2) return [];
  const n = points.length;
  const k = Math.min(5, n - 1);
  let recentDelta = 0;
  for (let i = n - k; i < n; i++) recentDelta += points[i].cost - points[i - 1].cost;
  recentDelta /= k;

  // Average consecutive-record change grouped by calendar month (seasonality).
  const monthDeltas = {};
  for (let i = 1; i < n; i++) {
    const m = points[i].date.getMonth();
    (monthDeltas[m] = monthDeltas[m] || []).push(points[i].cost - points[i - 1].cost);
  }
  const seasonalFor = (date) => {
    const arr = monthDeltas[date.getMonth()];
    if (!arr || !arr.length) return null;
    return arr.reduce((a, b) => a + b, 0) / arr.length;
  };

  const out = [];
  let prevCost = fromCost != null ? fromCost : points[n - 1].cost;
  let prevDate = fromDate != null ? fromDate : points[n - 1].date;
  for (let h = 1; h <= 3; h++) {
    const d = new Date(prevDate.getTime() + WEEK);
    const seas = seasonalFor(d);
    const delta = seas != null ? 0.5 * recentDelta + 0.5 * seas : recentDelta;
    const cost = Math.round((prevCost + delta) * 100) / 100;
    out.push({ date: d, cost });
    prevCost = cost;
    prevDate = d;
  }
  return out;
}

function MilkPowderCostHistoryModal({ product, onClose, onChanged }) {
  const [rows, setRows] = useState([]);      // recorded rows (table)
  const [series, setSeries] = useState([]);  // continuous weekly series (chart)
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  // Index (into the weekly series / forecast) the cursor is nearest to.
  const hoverRef = useRef(null);

  // 12-month viewing window with back/forward paging over the full weekly span.
  const extent = useMemo(() => {
    const ts = (series || []).map(r => parseDay(r.week_commencing).getTime()).filter(Number.isFinite);
    return ts.length ? [Math.min(...ts), Math.max(...ts)] : [null, null];
  }, [series]);
  const win = useTimeWindow(extent);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const base = `${API_URL}/api/pricing/milk-powder/cost-prices/${product.inventory_item_id}`;
      const [hRes, sRes] = await Promise.all([
        fetch(`${base}/history`, { credentials: 'include' }),
        fetch(`${base}/series`, { credentials: 'include' })
      ]);
      const h = await hRes.json();
      const sData = await sRes.json();
      setRows(Array.isArray(h) ? h : []);
      setSeries(Array.isArray(sData) ? sData : []);
    } catch (e) {
      console.error('Error loading cost history:', e);
      setRows([]);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  }, [product.inventory_item_id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleDelete = (row) => {
    setConfirmDialog({
      isOpen: true,
      message: `Delete the cost for week commencing ${formatDate(row.week_commencing)}?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          await fetch(`${API_URL}/api/pricing/milk-powder/cost-prices/${row.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          await loadHistory();
          if (onChanged) onChanged();
        } catch (e) {
          console.error('Error deleting cost row:', e);
        }
      }
    });
  };

  // Build the chart datasets from the history rows.
  /* Re-read on a theme change: a canvas keeps the colours it was painted
     with, so without this the chart stayed in the old palette. */
  const themeColors = useThemeColors({
    textPrimary: '--text-primary',
    textPrimaryRgb: '--text-primary-rgb',
    textSecondary: '--text-secondary',
    bgTertiary: '--bg-tertiary'
  }, {
    textPrimary: '#fff',
    textPrimaryRgb: '255, 255, 255',
    textSecondary: '#c2c2c2',
    bgTertiary: '#2f2f2f'
  });

  const chart = useMemo(() => {
    if (!series || series.length < 1) return null;
    // Weekly carry-forward series across the fixed window (today − 12mo − 3wk →
    // this week). Each week's cost is the cost in force then (carried forward);
    // `recorded` weeks (a new cost was set) draw solid, carried weeks dotted.
    // A 3-week forecast (from the recorded points) is appended for the future.
    const asc = [...series]
      .map(r => ({
        ts: parseDay(r.week_commencing).getTime(),
        recorded: !!r.recorded,
        cost: r.cost != null ? parseFloat(r.cost) : null
      }))
      .filter(p => Number.isFinite(p.cost))
      .sort((a, b) => a.ts - b.ts);
    if (asc.length < 2) return null;
    const assumedAt = asc.map(p => !p.recorded);

    const colors = themeColors;

    // Rainbow gradient (orange → pink → purple → blue) for the line + fill.
    const RAINBOW = ['255, 122, 89', '255, 93, 177', '155, 109, 255', '74, 168, 255'];
    const rainbowGradient = (c, area, alpha) => {
      const g = c.createLinearGradient(area.left, 0, area.right, 0);
      RAINBOW.forEach((rgb, i) => g.addColorStop(i / (RAINBOW.length - 1), `rgba(${rgb}, ${alpha})`));
      return g;
    };
    const rainbowBorder = (ctx) => {
      const { chartArea, ctx: c } = ctx.chart;
      if (!chartArea) return `rgba(${RAINBOW[0]}, 1)`;
      return rainbowGradient(c, chartArea, 1);
    };
    const DOT_DASH = [2, 4];

    // Forecast: trend/seasonality from the recorded cost changes, anchored at the
    // end of the carried-forward series so the projected weeks extend past today.
    // Only drawn on the CURRENT window (offset 0) — paging back into history
    // shows recorded cost only, never a projection over dates already known.
    const lastAsc = asc[asc.length - 1];
    const recordedPts = asc.filter(p => p.recorded).map(p => ({ date: new Date(p.ts), cost: p.cost }));
    const forecast = (win.isCurrent && recordedPts.length >= 2)
      ? buildForecast(recordedPts, new Date(lastAsc.ts), lastAsc.cost)
      : [];
    // Projected points only past today, per requirement.
    const forecastData = forecast.length
      ? [{ x: lastAsc.ts, y: lastAsc.cost }, ...forecast.map(f => ({ x: f.date.getTime(), y: f.cost }))]
          .filter(d => d.x >= win.todayTs)
      : [];

    // Keep the full series in the dataset; clip to the window via the x-axis.
    const actualData = asc.map(p => ({ x: p.ts, y: p.cost, recorded: p.recorded }));
    const xMin = win.startTs;
    const xMax = win.isCurrent
      ? Math.max(win.endTs, forecastData.length ? forecastData[forecastData.length - 1].x : win.endTs)
      : win.endTs;

    // Y-range from what's actually visible in this window.
    const visible = [...actualData, ...forecastData].filter(d => d.x >= xMin && d.x <= xMax);
    const ys = (visible.length ? visible : actualData).map(d => d.y);
    const yLo = Math.min(...ys), yHi = Math.max(...ys);
    const yPad = (yHi - yLo || yHi || 1) * 0.18;

    // Month ticks across the window.
    const monthTicks = [];
    const mt = new Date(xMin); mt.setDate(1); mt.setHours(0, 0, 0, 0);
    if (mt.getTime() < xMin) mt.setMonth(mt.getMonth() + 1);
    while (mt.getTime() <= xMax) { monthTicks.push(mt.getTime()); mt.setMonth(mt.getMonth() + 1); }

    const data = {
      datasets: [
        {
          label: 'Cost / tonne',
          data: actualData,
          borderColor: rainbowBorder,
          fill: 'start',
          backgroundColor: (ctx) => {
            const { chart } = ctx; const { ctx: c, chartArea } = chart;
            if (!chartArea) return 'transparent';
            return rainbowGradient(c, chartArea, 0.22);
          },
          // A dot on every recorded week inside the window.
          pointRadius: (ctx) => {
            const p = ctx.raw;
            return (p && p.recorded && p.x >= win.startTs && p.x <= win.endTs) ? 2.6 : 0;
          },
          pointHoverRadius: 0,
          pointBackgroundColor: colors.textPrimary,
          pointBorderWidth: 0,
          borderWidth: 1.5,
          borderCapStyle: 'round',
          tension: 0.4,
          spanGaps: true,
          segment: { borderDash: (ctx) => (assumedAt[ctx.p1DataIndex] ? DOT_DASH : undefined) }
        },
        {
          label: 'Projected (3 wks)',
          data: forecastData,
          borderColor: colors.textSecondary,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 1.5,
          borderCapStyle: 'round',
          borderDash: DOT_DASH,
          tension: 0.4
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      layout: { padding: { top: 22, left: 0, right: 0, bottom: 0 } },
      plugins: { legend: { display: false }, tooltip: { enabled: false }, title: { display: false } },
      onHover: (e, els, chart) => {
        if (!e || e.x == null || !els || !els.length) {
          if (hoverRef.current) { hoverRef.current = null; chart.draw(); }
          return;
        }
        let best = null, bd = Infinity;
        for (const a of els) {
          const el = chart.getDatasetMeta(a.datasetIndex).data[a.index];
          const dp = chart.data.datasets[a.datasetIndex]?.data?.[a.index];
          if (!dp || dp.x < win.startTs || dp.x > xMax) continue;
          const d = Math.abs(el.x - e.x);
          if (d < bd) { bd = d; best = { datasetIndex: a.datasetIndex, index: a.index }; }
        }
        hoverRef.current = best;
        chart.draw();
      },
      scales: {
        x: {
          type: 'linear', min: xMin, max: xMax,
          grid: { display: false }, border: { display: false },
          afterBuildTicks: (axis) => { axis.ticks = monthTicks.map(v => ({ value: v })); },
          ticks: { color: colors.textSecondary, font: { size: 10 }, maxRotation: 0, autoSkip: false, callback: (val) => new Date(val).toLocaleDateString('en-GB', { month: 'short' }) }
        },
        y: { display: false, min: yLo - yPad, max: yHi + yPad }
      }
    };

    // Hover crosshair: dashed vertical line, a dot, and a cost · date pill.
    // Carried-forward weeks show the cost bracketed; forecast weeks "(projected)".
    const crosshair = {
      id: 'mpCrosshair',
      afterDatasetsDraw(chart) {
        const hit = hoverRef.current;
        if (!hit) return;
        const el = chart.getDatasetMeta(hit.datasetIndex)?.data?.[hit.index];
        const dp = chart.data.datasets[hit.datasetIndex]?.data?.[hit.index];
        if (!el || !dp) return;
        const { ctx, chartArea } = chart;
        const x = el.x;

        ctx.save();
        const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, `rgba(${colors.textPrimaryRgb}, 0.55)`);
        g.addColorStop(1, `rgba(${colors.textPrimaryRgb}, 0)`);
        ctx.beginPath(); ctx.setLineDash([4, 4]); ctx.lineWidth = 1; ctx.strokeStyle = g;
        ctx.moveTo(x, chartArea.bottom); ctx.lineTo(x, chartArea.top); ctx.stroke(); ctx.setLineDash([]);

        ctx.beginPath(); ctx.fillStyle = colors.textPrimary; ctx.arc(x, el.y, 3.5, 0, Math.PI * 2); ctx.fill();

        const isForecast = hit.datasetIndex === 1;
        const assumed = !isForecast && assumedAt[hit.index];
        const valStr = isForecast ? `${formatCurrency(dp.y)} (projected)` : (assumed ? `(${formatCurrency(dp.y)})` : formatCurrency(dp.y));
        const label = `${valStr}  ·  ${shortDate(new Date(dp.x))}`;
        ctx.font = '600 11px sans-serif';
        const padX = 9;
        const w = ctx.measureText(label).width + padX * 2;
        const h = 20;
        let px = x - w / 2;
        px = Math.max(chartArea.left, Math.min(px, chartArea.right - w));
        const py = Math.max(0, chartArea.top - h - 4);
        const r = 10;
        ctx.beginPath();
        ctx.moveTo(px + r, py);
        ctx.arcTo(px + w, py, px + w, py + h, r);
        ctx.arcTo(px + w, py + h, px, py + h, r);
        ctx.arcTo(px, py + h, px, py, r);
        ctx.arcTo(px, py, px + w, py, r);
        ctx.closePath();
        ctx.fillStyle = colors.bgTertiary;
        ctx.fill();
        ctx.fillStyle = colors.textPrimary;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.fillText(label, px + padX, py + h / 2 + 0.5);
        ctx.restore();
      }
    };

    return { data, options, plugins: [crosshair] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, win.startTs, win.endTs, win.isCurrent, win.todayTs, themeColors]);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Cost History — ${product.item_name}`}
      size="md"
      className="milk-powder-history"
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="table-container-short">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ borderTopLeftRadius: "var(--radius-md)" }}>Week Commencing</th>
                  <th className="text-right">Cost</th>
                  <th>Notes</th>
                  <th className="actions-col" style={{ borderTopRightRadius: "var(--radius-md)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="4" className="text-center">No cost history yet</td></tr>
                ) : (
                  rows.map((row, i) => (
                    <tr key={row.id}>
                      <td>
                        {formatDate(row.week_commencing)}
                        {i === 0 && <span className="current-cost-badge">CURRENT</span>}
                      </td>
                      <td className="text-right"><div className="price">{formatCurrency(row.cost)}</div></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>{row.notes || ''}</td>
                      <td className="actions-col">
                        <button className="btn-icon" onClick={() => handleDelete(row)} title="Delete this week">
                          <span className="material-symbols-rounded">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {chart && (
            <div className="mp-history-chart">
              <Line data={chart.data} options={chart.options} plugins={chart.plugins} />
              <ChartWindowNav
                canBack={win.canBack}
                canForward={win.canForward}
                onBack={win.goBack}
                onForward={win.goForward}
                label={win.label}
              />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </Modal>
  );
}

export default MilkPowderCostHistoryModal;
